/* eslint-disable max-lines */
import type { User, UserBase } from '@recruitee/account-types';
import type { Fieldset, FieldTemplate } from '@recruitee/custom-fields-types';
import type { Country, LocationBrief } from '@recruitee/locations-types';
import type { PipelineTemplateItem } from '@recruitee/pipeline-templates-types';
import { EditorJSONState } from '@recruitee/prosemirror-types';

import type { OfferKind, OfferStatus } from './offers.enums';

// copy due to circular dependency
enum RequisitionStatus {
  Draft = 'draft',
  Pending = 'pending',
  Rejected = 'rejected',
  Approved = 'approved',
  Filled = 'filled',
}

export enum CandidateInfoFieldOptionValue {
  Required = 'required',
  Optional = 'optional',
  Off = 'off',
}

export type RequisitionBrief = { id: number; title: string; status: RequisitionStatus };

export type OfferLanguageItem = {
  translationFieldsCount: number;
  primary: boolean;
  nativeName: string;
  name: string;
  enabled: boolean;
  code: string;
  offerId?: number;
  offerTemplateId?: number;
  missingTranslationFields: JobTranslatableFields;
};

export type JobTranslatableFields = {
  autoReplyTemplateFields: string[];
  offerFields: string[];
  openQuestionIds: number[];
};

export type EnabledOfferLanguageItem = Pick<OfferLanguageItem, 'code' | 'name' | 'nativeName'>;

export interface OfferBrief {
  createdAt: string;
  id: number;
  guid: string;
  departmentId: number | null;
  department?: string | null;
  kind: TOfferKind;
  langCode: string;
  location: string;
  locationIds: number[];
  position: number;
  slug: string;
  status: OfferStatus;
  title: string;
  description: string;
  descriptionJson?: EditorJSONState | null;
  requirements: string;
  requirementsJson?: EditorJSONState | null;
  // references
  locations?: LocationBrief[];
  pipelineTemplateId?: number;
  url?: string;
  hiringManagerId?: number | null;
  recruiterId?: number | null;
  recruiter?: User;
  hiringManager?: User;
  mailboxEmail?: string;
  hybrid: boolean;
  onSite: boolean;
  remote: boolean;
}

export interface ViewboxOffer {
  id: number;
  kind: TOfferKind;
  status: OfferStatus;
  title: string;
}

export interface OfferBriefReference extends OfferBrief {
  type: 'Offer';
}

export interface OfferItem extends OfferBrief {
  adminappUrl: string;
  candidatesCount: number;
  careersUrl: string;
  city: string;
  closedAt: string | null;
  countryCode: string;
  disqualifiedCandidatesCount: number;
  eeoSettings: EEOSettings | null;
  enabledForReferrals: boolean;
  enabledLanguages: EnabledOfferLanguageItem[];
  employmentType: EmploymentType;
  example?: boolean;
  followed: boolean;
  followers: UserBase[];
  followerIds?: number[];
  hasActiveCampaign: boolean;

  issues?: JobIssues;
  jobScheduler: JobSchedulerData | null;
  locations?: LocationBrief[];
  locationsQuestion: string;
  locationsQuestionRequired: boolean;
  locationsQuestionType: LocationsQuestionKind;
  newCandidatesCount?: number;
  offerTags: string[];
  tags: Array<{ id: number; name: string }>;

  pipeline: boolean;
  pipelineTemplate: Omit<PipelineTemplateItem, 'assignedOffersCount'> | null;
  postalCode: string;
  priority: Priority | null;
  publishedAt: string | null;
  qualifiedCandidatesCount: number;
  sharedOpeningsCount?: number;
  stateCode: string;
  stateName: string;
  street: string;
  updatedAt: string;
  hiredCandidatesCount: number;
  hiredCandidatesWithoutOpeningsCount: number;
  numberOfOpenings: number | null;
  requisitions: RequisitionBrief[];
}

export interface OfferItemReference extends OfferItem {
  type: 'Offer';
}

export interface OfferFull extends OfferItem {
  admins: User[];
  attachmentsCount: number;
  autoReplyTemplateId: number | null;
  category: Category | null;
  coverImage: string | null;
  coverImageUrl?: string | null;
  defaultTranslations: {
    title: string;
    description: string;
    emailConfirmationSubject: string;
    locationsQuestion: string;
  };
  education: OfferEducation;
  emailConfirmation: boolean;
  emailConfirmationBody: string;
  emailConfirmationSubject: string;
  experience: OfferExperience;
  fields: FieldTemplate[];
  fieldset: Fieldset;
  fieldsetId: number | null;
  hasAdditionalInfo: boolean;
  maxHoursPerWeek: string;
  minHoursPerWeek: string;
  newCandidatesCount?: number;
  notesCount: number;
  openQuestionTemplateId: number | null;
  optionsCoverLetter: CandidateInfoFieldOptionValue;
  optionsCv: CandidateInfoFieldOptionValue;
  optionsPhone: CandidateInfoFieldOptionValue;
  optionsPhoto: CandidateInfoFieldOptionValue;
  pipelineTemplateId: number;
  primaryLangCode: string;
  salary: SalaryData;
  sharingTitle: string;
  sharingDescription: string;
  sharingImage: string | null;
  sharingImageUrl?: string | null;
  visibilityOptions: string[];
  highlightJson: EditorJSONState | null;
  wysiwygEditor: 'prosemirror' | 'redactor';
}

export interface SalaryData {
  currency: string | null;
  max: string | null;
  min: string | null;
  period: SalaryPeriod | null;
}

export enum SalaryPeriod {
  Year = 'year',
  Month = 'month',
  Hour = 'hour',
}
export interface OfferWithCountry extends OfferFull {
  country: Country;
}

export type TOfferKind = `${OfferKind}`;
export type TOfferStatus = `${OfferStatus}`;

export enum OfferViewMode {
  Default = 'default',
  Brief = 'brief',
}

export interface OfferData extends Partial<OfferFull> {
  adminIds?: number[];
  department?: string;
  description?: string;
  fields?: FieldTemplate[];
  fieldsetId?: number | null;
  id?: number;
  kind?: OfferKind;
  title?: string;
  tagIds?: number[];
}

export type OfferLanguagesSetup = {
  translationFieldsCount: number;
  languages: OfferLanguageItem[];
  translationFields: JobTranslatableFields;
};

export enum PipelineSortOption {
  Name = 'name',
  Evaluation = 'positive_ratings',
  CreatedAt = 'created_at',
  New = 'viewed',
  Overdue = 'overdue_at',
  JobFitScore = 'job_fit_score',
}

export enum JobSchedulerError {
  SlotsLimit = 'slots_limit_exceeded',
  InvalidStatus = 'invalid_status',
  MissingData = 'missing_data',
  MissingRequisition = 'missing_requisition',
  LocationsLimitExceeded = 'locations_limit_exceeded',
}

export type JobSchedulerData = {
  publishDate: string;
  closeDate: string;
  timezone: string;
  error: JobSchedulerError | null;
} | null;

export type EEOSettings = {
  enabled: boolean;
  jobCategory?: string;
};

export interface JobIssues {
  isRequiredDataMissing: boolean;
  isRequisitionMissing: boolean;
  isMigratedHtmlEqual?: boolean;
}

export enum JobErrorFieldKey {
  SlotsLimitExceeded = 'slotsLimitExceeded',
  PrimaryLangCode = 'primaryLangCode',
  Description = 'description',
  Requirements = 'requirements',
}

export enum LocationsQuestionKind {
  SingleChoice = 'single_choice',
  MultipleChoice = 'multiple_choice',
}

export enum EmploymentType {
  FullTime = 'fulltime',
  FullTimePermanent = 'fulltime_permanent',
  FullTimeFixedTerm = 'fulltime_fixed_term',
  PartTime = 'parttime',
  PartTimePermanent = 'parttime_permanent',
  PartTimeFixedTerm = 'parttime_fixed_term',
  PartTimeMinijob = 'parttime_minijob',
  Freelance = 'freelance',
  Contract = 'contract',
  Temporary = 'temporary',
  Internship = 'internship',
  Apprenticeship = 'apprenticeship',
  Seasonal = 'seasonal',
  Volunteer = 'volunteer',
}

export enum OfferEducation {
  HighSchoolCoursework = 'high_school_coursework',
  HighSchool = 'high_school',
  Certification = 'certification',
  Vocational = 'vocational',
  CollegeCoursework = 'college_coursework',
  AssociateDegree = 'associate_degree',
  BachelorDegree = 'bachelor_degree',
  MasterDegree = 'master_degree',
  Doctorate = 'doctorate',
  Professional = 'professional',
}

export enum OfferExperience {
  StudentSchool = 'student_school',
  StudentCollege = 'student_college',
  EntryLevel = 'entry_level',
  MidLevel = 'mid_level',
  Experienced = 'experienced',
  Manager = 'manager',
  SeniorManager = 'senior_manager',
  Executive = 'executive',
  SeniorExecutive = 'senior_executive',
}

export enum Category {
  Accountancy = 'accountancy',
  Administrative = 'administrative',
  Advertising = 'advertising',
  Agriculture = 'agriculture',
  ArchitecturalServices = 'architectural_services',
  ArtsEntertainment = 'arts_entertainment',
  Automotive = 'automotive',
  Banking = 'banking',
  BiotechPharma = 'biotech_pharma',
  Cleaning = 'cleaning',
  Construction = 'construction',
  Consulting = 'consulting',
  CustomerService = 'customer_service',
  Design = 'design',
  Education = 'education',
  Energy = 'energy',
  Engineering = 'engineering',
  Finance = 'finance',
  GovernmentNonprofit = 'government_nonprofit',
  Healthcare = 'healthcare',
  Hospitality = 'hospitality',
  InformationTechnology = 'information_technology',
  Insurance = 'insurance',
  Internet = 'internet',
  LegalServices = 'legal_services',
  Leisure = 'leisure',
  Logistics = 'logistics',
  Management = 'management',
  Manufacturing = 'manufacturing',
  MarketingPR = 'marketing_pr',
  Procurement = 'procurement',
  Property = 'property',
  Publishing = 'publishing',
  RecruitmentHr = 'recruitment_hr',
  Retail = 'retail',
  Sales = 'sales',
  Security = 'security',
  Science = 'science',
  Technical = 'technical',
  Telecommunication = 'telecommunication',
  Tourism = 'tourism',
  TranslationServices = 'translation_services',
  Other = 'other',
}

export enum Priority {
  Low = 'low',
  Normal = 'normal',
  High = 'high',
  Urgent = 'urgent',
}
